import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Bold, Italic, Underline as UIcon, List, ListOrdered, Heading1, Heading2, Link2, Quote, Code, Eraser } from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder, testId = "rte" }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-sky-600 underline" } }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
        editorProps: {
            attributes: {
                class: "prose prose-slate dark:prose-invert max-w-none min-h-[200px] p-4 outline-none",
                "data-testid": `${testId}-area`,
            },
        },
    });

    useEffect(() => {
        if (editor && value !== undefined && value !== editor.getHTML()) {
            editor.commands.setContent(value || "", false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    if (!editor) return null;

    const btn = (active) => `p-1.5 rounded-md transition-colors ${active ? "bg-sky-500 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`;

    return (
        <div data-testid={testId} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                <button type="button" data-testid={`${testId}-h1`} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btn(editor.isActive("heading", { level: 1 }))}><Heading1 className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-h2`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn(editor.isActive("heading", { level: 2 }))}><Heading2 className="h-4 w-4" /></button>
                <div className="w-px h-5 bg-slate-300 dark:bg-slate-600 mx-1" />
                <button type="button" data-testid={`${testId}-bold`} onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive("bold"))}><Bold className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-italic`} onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive("italic"))}><Italic className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-underline`} onClick={() => editor.chain().focus().toggleUnderline().run()} className={btn(editor.isActive("underline"))}><UIcon className="h-4 w-4" /></button>
                <div className="w-px h-5 bg-slate-300 dark:bg-slate-600 mx-1" />
                <button type="button" data-testid={`${testId}-ul`} onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive("bulletList"))}><List className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-ol`} onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive("orderedList"))}><ListOrdered className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-quote`} onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn(editor.isActive("blockquote"))}><Quote className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-code`} onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={btn(editor.isActive("codeBlock"))}><Code className="h-4 w-4" /></button>
                <div className="w-px h-5 bg-slate-300 dark:bg-slate-600 mx-1" />
                <button type="button" data-testid={`${testId}-link`} onClick={() => {
                    const url = window.prompt("URL:", editor.getAttributes("link").href || "https://");
                    if (url === null) return;
                    if (url === "") editor.chain().focus().unsetLink().run();
                    else editor.chain().focus().setLink({ href: url }).run();
                }} className={btn(editor.isActive("link"))}><Link2 className="h-4 w-4" /></button>
                <button type="button" data-testid={`${testId}-clear`} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} className={btn(false)}><Eraser className="h-4 w-4" /></button>
            </div>
            <EditorContent editor={editor} />
            {placeholder && !value && <div className="px-4 py-2 text-xs italic text-slate-400 -mt-32 pointer-events-none">{placeholder}</div>}
        </div>
    );
}
